import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/shared/ui/buttons/Button';
import { ChevronDownIcon, ChevronRightIcon } from '@/shared/ui/icons';
import { formatUsd } from '@/shared/utils/currency';
import type { ApiSplitTarget } from '@/shared/types/api/splits';

const Row = ({ label, value }: { label: string; value: string | null | undefined }) => (
  <div className="flex items-start justify-between gap-3 py-1.5 text-sm">
    <span className="text-ink-muted">{label}</span>
    <span className="text-right font-medium text-ink">{value || '—'}</span>
  </div>
);

// One recipient (PINFL) as a collapsible card. Self-contained expand state so
// each card toggles independently and the parent list keeps a stable hook count.
export const FeaturesSplitsTargetCard = ({ target }: { target: ApiSplitTarget }) => {
  const { t } = useTranslation('splits');
  const [open, setOpen] = useState(false);

  const fullAddress = `${target.street}, ${target.building}${
    target.apartment ? `/${target.apartment}` : ''
  }, ${target.city}, ${t(`regions:${target.region}`)}`;

  return (
    <div className="flex flex-col gap-1 rounded-lg border border-slate-200 bg-white p-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-ink">
            {`${target.last_name} ${target.first_name}`}
          </p>
          <p className="text-xs text-ink-muted">{target.pinfl}</p>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          rightIcon={
            open ? (
              <ChevronDownIcon className="h-4 w-4" />
            ) : (
              <ChevronRightIcon className="h-4 w-4" />
            )
          }
          onClick={() => setOpen((v) => !v)}
        >
          {open ? t('detailsHide') : t('detailsShow')}
        </Button>
      </div>

      {open && (
        <div className="mt-1 flex flex-col gap-2 border-t border-slate-100 pt-2">
          <p className="text-xs text-ink-soft">{fullAddress}</p>
          <Row label={t('fields.phone')} value={target.phone_number} />
          <Row label={t('fields.passport')} value={target.passport_series} />

          <div className="mt-1">
            <p className="text-xs font-semibold uppercase tracking-wide text-ink-muted">
              {t('packageInfo')}
            </p>
            {target.package_id && (
              <p className="text-xs text-ink-muted">#{target.package_id}</p>
            )}
            <Row label={t('totalAmount')} value={formatUsd(target.total_amount)} />
            <Row label={t('reservedAmount')} value={formatUsd(target.reserved_amount)} />
          </div>

          <div className="mt-1">
            <p className="text-xs font-semibold uppercase tracking-wide text-ink-muted">
              {t('allocatedProducts')}
            </p>
            {target.allocations.length === 0 ? (
              <p className="text-sm text-ink-muted">{t('targetsEmpty')}</p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {target.allocations.map((a) => (
                  <li key={a.id} className="flex items-center justify-between py-1.5 text-sm">
                    <span className="text-ink-soft">
                      {a.quantity != null ? `× ${a.quantity}` : '—'}
                    </span>
                    <span className="font-medium text-ink">
                      {a.amount != null ? formatUsd(a.amount) : '—'}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
